import { random } from 'lodash'
import { DateTime, Info } from 'luxon'
import {
  addCents,
  dollarsToCents,
  formatCurrencyFromCents,
} from '@/utils/currencyHelpers'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { patternFormatter } from 'react-number-format'
import currency from 'currency.js'

// TODO: Migrate V2
import { DropdownOption } from '@/componentsV1/BaseComponents'
import { Colors } from '../../../styles/theme'

export enum INSIGHTS_CHANGE_TYPE {
  INCREASE = 'increase',
  DECREASE = 'decrease',
  SAME = 'same',
  EMPTY = 'empty',
  LAST_MONTH_EMPTY = 'last_month_empty',
}

export enum ARTICLE_TYPE {
  ARTICLE = 'article',
  WEBINAR = 'webinar',
}

export enum INSIGHTS_TYPE {
  EXPENSES = 'expenses',
  PROFIT = 'profit',
  INCOME = 'income',
}

export enum CATEGORY_FILTERS {
  ALL = 'all',
  TOP = 'top',
  DEDUCTIBLE = 'deductible',
}

export interface ChartAPIResponse {
  month_number: number
  month_abbreviation: string
  year: number
  transaction_category_id: number
  transaction_category_identifier: string
  transaction_category_name: string
  transaction_category_sum_in_cents: string
  is_tax_deductible: boolean
  total_expenses?: number
  total_income?: number
}

export interface SummaryAPIResponse {
  target_month_name: string
  target_month_total_in_cents: number
  ytd_monthly_average_in_cents: number
  ytd_total_in_cents: number
  previous_month_total_in_cents: number
  previous_month_name: string
  percent_change_from_previous_month_decimal: number
  percent_of_target_months_counterpart_decimal: number
}

export interface ChartCategories {
  [category_identifier: string]: {
    id: number
    total_amount: number
    category_name: string
  }
}

export interface UncategorizedTransactionResponse {
  total_transactions: number
  total_amount_in_cents: number
  needs_review: boolean
}

export interface InsightsCashflowResponse {
  totalBalanceInCents: number
  asOfDate: string
  priorMonthsEndingBalance: number
  percentageChange: number
}

export interface InsightsKeyWidget {
  key: string
}

export const getInsightsDateRange = (endDate: DateTime): [string, string] => {
  const startDate = endDate.minus({ months: 11 }).startOf('month')

  return [
    startDate.toFormat(DATE_FORMATS_LUXON.TIMESTAMP),
    endDate.toFormat(DATE_FORMATS_LUXON.TIMESTAMP),
  ]
}

export const getInsightsMonthYear = (endDate: DateTime) => ({
  month: endDate.month,
  year: endDate.year,
})

interface MonthData {
  [month: string]: {
    month: string
    month_number: number
    year: number
  }
}

export const generateMonthMap = (dateRange: [string, string]) => {
  const [startDateString, endDateString] = dateRange

  const startDate = DateTime.fromFormat(
    startDateString,
    DATE_FORMATS_LUXON.TIMESTAMP
  )
  const endDate = DateTime.fromFormat(
    endDateString,
    DATE_FORMATS_LUXON.TIMESTAMP
  )

  const monthMap: MonthData = {}

  for (
    let currentMonth = startDate;
    currentMonth <= endDate;
    currentMonth = currentMonth.plus({ months: 1 })
  ) {
    const month = currentMonth.toFormat(DATE_FORMATS_LUXON.MONTH_ABBR)
    monthMap[month] = {
      month,
      month_number: currentMonth.month,
      year: currentMonth.year,
    }
  }

  return monthMap
}

export const findTopCategories = (categories: ChartCategories, top: number) => {
  const categoryIdentifiers = Object.keys(categories)
  categoryIdentifiers.sort(
    (a, b) => categories[b].total_amount - categories[a].total_amount
  )
  const topCategories = categoryIdentifiers.slice(0, top)
  return topCategories
}

export const getCategoryData = (data: ChartAPIResponse[]) => {
  const chartCategories: ChartCategories = {}
  for (const item of data) {
    const {
      transaction_category_id,
      transaction_category_identifier,
      transaction_category_name,
      transaction_category_sum_in_cents,
    } = item
    if (!chartCategories[transaction_category_identifier]) {
      chartCategories[transaction_category_identifier] = {
        id: transaction_category_id,
        total_amount: parseInt(transaction_category_sum_in_cents),
        category_name: transaction_category_name,
      }
    } else {
      chartCategories[transaction_category_identifier].total_amount = addCents(
        chartCategories[transaction_category_identifier].total_amount,
        parseInt(transaction_category_sum_in_cents)
      )
    }
  }
  for (const category of Object.keys(chartCategories)) {
    chartCategories[category].total_amount = Math.abs(
      chartCategories[category].total_amount
    )
  }
  const topCategories = findTopCategories(chartCategories, 3)
  return {
    chartCategories,
    topCategories,
  }
}

export const processDataForChart = (
  data: ChartAPIResponse[],
  dateRange: [string, string],
  type: INSIGHTS_TYPE
) => {
  const months = generateMonthMap(dateRange)
  const chartKeys: {
    [month: string]: {
      [category_identifier: string]: number | string | undefined
      month: string
      month_number: number
      year: number
      total_expenses?: number
      total_income?: number
    }
  } = { ...months }
  for (const item of data) {
    const {
      month_abbreviation,
      transaction_category_identifier,
      transaction_category_sum_in_cents,
      total_expenses,
      total_income,
      year,
    } = item
    chartKeys[month_abbreviation] = {
      ...chartKeys[month_abbreviation],
      year,
      [transaction_category_identifier]: parseInt(
        transaction_category_sum_in_cents
      ),
    }
    if (type === INSIGHTS_TYPE.EXPENSES) {
      chartKeys[month_abbreviation][transaction_category_identifier] =
        -(chartKeys[month_abbreviation][
          transaction_category_identifier
        ] as number)
    }
    if (type === INSIGHTS_TYPE.PROFIT) {
      chartKeys[month_abbreviation].total_expenses = total_expenses
      chartKeys[month_abbreviation].total_income = total_income
    }
  }
  const chartData = Object.values(chartKeys)
  chartData.sort((a, b) => {
    if (a.year === b.year) {
      return a.month_number - b.month_number
    } else {
      return a.year - b.year
    }
  })
  return chartData
}

export const filterChartAPIResponse = (
  data: ChartAPIResponse[],
  filter: string | null,
  topCategories: string[] = []
) => {
  if (filter === 'all' || !filter) return data
  if (filter === 'top')
    return data.filter((item) =>
      topCategories.includes(item.transaction_category_identifier)
    )
  if (filter === 'deductible')
    return data.filter((item) => item.is_tax_deductible)
  return data.filter((item) => item.transaction_category_identifier === filter)
}

export const filterChartCategories = (
  categories: ChartCategories,
  filter: string | null,
  topCategories: string[] = []
) => {
  const filteredCategories: ChartCategories = {}
  if (filter === 'all' || !filter) return categories
  if (filter === 'top') {
    topCategories.forEach((categoryIdentifier) => {
      if (categories[categoryIdentifier]) {
        filteredCategories[categoryIdentifier] = categories[categoryIdentifier]
      }
    })
  } else if (categories[filter]) {
    filteredCategories[filter] = categories[filter]
  }
  return filteredCategories
}

const chartColors = [
  Colors.magenta,
  Colors.teal,
  Colors.orange,
  Colors.yellow,
  Colors.blue,
  Colors.red,
  Colors.forest,
]

export const getBarColor = (id: number) => {
  return chartColors[id % chartColors.length]
}

export const EXAMPLE_CATEGORIES = [
  'Payroll: Contractors',
  'Rent & Lease',
  'Communication',
  'Software Services',
  'Accounting Fees',
  'Travel',
  'Payroll: Employee Benefits',
  'Payroll: Reimbursable Expenses',
  'Internet',
  'Account Receivable',
  'Accounting Fees',
  'Continuing Education',
  'Professional Development',
]

export const generateSampleInsightsRowAPIResponses =
  (): InsightsRowAPIResponse[] => {
    const TOTAL_BUDGET_CENTS = -10000

    const initialResults = EXAMPLE_CATEGORIES.map((category) => {
      const randomValue = Math.random()

      return {
        start_date: '2024-01-01',
        end_date: '2024-12-31',
        transaction_category_id: Math.floor(Math.random() * 100) + 1,
        transaction_category_name: category,
        transactions_count: Math.floor(Math.random() * 10) + 1,
        is_tax_deductible: Math.random() < 0.5,
        percent_of_total_cents_decimal: randomValue,
        transactions_sum_in_cents: 0, // Will be calculated after normalization
      }
    })

    const totalRandomValues = initialResults.reduce(
      (sum, item) =>
        currency(sum).add(item.percent_of_total_cents_decimal).value,
      0
    )

    // Normalize the values so they sum to 1.0 (100%) and calculate corresponding sums
    return initialResults.map((item) => {
      const normalizedPercentage = currency(
        item.percent_of_total_cents_decimal
      ).divide(totalRandomValues).value

      const transactionsSumInCents = dollarsToCents(
        currency(TOTAL_BUDGET_CENTS).multiply(normalizedPercentage)
      )

      return {
        ...item,
        percent_of_total_cents_decimal: normalizedPercentage,
        transactions_sum_in_cents: transactionsSumInCents,
      }
    })
  }

// function to generate sample InsightsTransactionsAPIResponse objects based on the transaction category ID
export const generateSampleInsightsTransactionsAPIResponses = (
  transactionCategoryId: number,
  insightsRows: InsightsRowAPIResponse[]
): InsightsTransactionsAPIResponse[] => {
  const transactions: InsightsTransactionsAPIResponse[] = []

  const matchingRow = insightsRows.find(
    (row) => row.transaction_category_id === transactionCategoryId
  )

  if (matchingRow) {
    for (let i = 0; i < matchingRow.transactions_count; i++) {
      const transaction: InsightsTransactionsAPIResponse = {
        id: Math.floor(Math.random() * 100) + 1, // Random transaction ID between 1 and 100
        date: new Date(
          2024,
          0,
          1 + Math.floor(Math.random() * 270)
        ).toISOString(),
        transaction_category_id: matchingRow.transaction_category_id,
        transaction_category_name: matchingRow.transaction_category_name,
        amount_in_cents: Math.floor(Math.random() * (20000 - 500 + 1)) - 500, // Random negative amount between -20000 and -500
        description: 'Sample Transaction',
      }

      transactions.push(transaction)
    }
  }

  return transactions
}

// function to create example response from API
export const getExampleExpenseChartData = () => {
  const exampleData: ChartAPIResponse[] = []
  const months = Info.months('short')

  months.forEach((month, i) => {
    EXAMPLE_CATEGORIES.forEach((category, idx) => {
      exampleData.push({
        month_number: i + 1,
        month_abbreviation: month,
        year: month === 'Jan' ? 2024 : 2023,
        transaction_category_id: idx,
        transaction_category_identifier: category,
        transaction_category_name: category,
        transaction_category_sum_in_cents: random(-10000, -300000).toString(),
        is_tax_deductible: true,
      })
    })
  })
  return exampleData
}

export const generateSampleSummaryExpenseData: SummaryAPIResponse[] = [
  {
    target_month_name: 'June',
    target_month_total_in_cents: -257084,
    ytd_monthly_average_in_cents: -201089,
    ytd_total_in_cents: -1443296,
    previous_month_total_in_cents: -470000,
    previous_month_name: 'May',
    percent_change_from_previous_month_decimal: -0.0638,
    percent_of_target_months_counterpart_decimal: -0.957,
  },
  {
    target_month_name: 'February',
    target_month_total_in_cents: 400000,
    ytd_monthly_average_in_cents: 420000,
    ytd_total_in_cents: 1680000,
    previous_month_total_in_cents: 450000,
    previous_month_name: 'January',
    percent_change_from_previous_month_decimal: -0.1111,
    percent_of_target_months_counterpart_decimal: 1.125,
  },
]

export const formatPercentage = (percentage?: number) => {
  if (!percentage) return '0%'
  let formattedPercentage = Math.abs(percentage * 100)
  if (formattedPercentage >= 1) {
    formattedPercentage = Math.round(formattedPercentage)
  } else if (formattedPercentage < 1) {
    // round to 1 decimal place for percentages under 1
    formattedPercentage = Math.round(formattedPercentage * 10) / 10
  }
  return `${formattedPercentage}%`
}

export const EXPENSE_BREAKDOWN_PAGE_SIZE = 8

export const isTopExpense = (
  topExpenseCategoryIds: number[],
  categoryId: number
) => {
  return topExpenseCategoryIds.indexOf(categoryId) >= 0
}

export const applyCategoryFilter = (
  expensesBreakdown: InsightsRowAPIResponse[],
  categoryFilter: CATEGORY_FILTERS | string,
  topExpenseCategoryIds: number[]
) => {
  return expensesBreakdown.filter((expenseBreakdown) => {
    if (categoryFilter === CATEGORY_FILTERS.ALL) {
      return true
    } else if (categoryFilter === CATEGORY_FILTERS.TOP) {
      return isTopExpense(
        topExpenseCategoryIds,
        expenseBreakdown.transaction_category_id
      )
    } else if (categoryFilter === CATEGORY_FILTERS.DEDUCTIBLE) {
      return expenseBreakdown.is_tax_deductible
    } else {
      return expenseBreakdown.transaction_category_name === categoryFilter
    }
  })
}

export interface InsightsRowAPIResponse {
  start_date: string
  end_date: string
  transaction_category_id: number
  transaction_category_name: string
  transactions_sum_in_cents: number
  transactions_count: number
  is_tax_deductible: boolean
  percent_of_total_cents_decimal: number
}

export interface InsightsTransactionsAPIResponse {
  id: number
  date: string
  transaction_category_id: number
  transaction_category_name: string
  amount_in_cents: number
  description: string
}

export const computeEndDateForInsightsExpensesBreakdownTable = (
  startDate: DateTime,
  endDate: DateTime,
  yearFilter: number,
  monthFilter: string
) => {
  const year = yearFilter

  if (monthFilter === 'ytd' && year === endDate.year) {
    return endDate
  } else if (monthFilter === 'ytd') {
    return startDate.endOf('year')
  } else {
    return startDate.endOf('month')
  }
}

export const MONTHS_ARR = [
  { text: 'Year to Date', value: 'ytd' },
  { text: 'January', value: '0' },
  { text: 'February', value: '1' },
  { text: 'March', value: '2' },
  { text: 'April', value: '3' },
  { text: 'May', value: '4' },
  { text: 'June', value: '5' },
  { text: 'July', value: '6' },
  { text: 'August', value: '7' },
  { text: 'September', value: '8' },
  { text: 'October', value: '9' },
  { text: 'November', value: '10' },
  { text: 'December', value: '11' },
]

export const formatSummary = (
  summaryData?: SummaryAPIResponse,
  insightsType?: INSIGHTS_TYPE
) => {
  if (!summaryData) return null
  const {
    target_month_name,
    previous_month_name,
    target_month_total_in_cents,
    ytd_monthly_average_in_cents,
    ytd_total_in_cents,
    percent_change_from_previous_month_decimal,
    percent_of_target_months_counterpart_decimal,
    previous_month_total_in_cents,
  } = summaryData
  return {
    month_name: target_month_name.trim(),
    previous_month_name: previous_month_name.trim(),
    month_total: formatCurrencyFromCents(
      insightsType === INSIGHTS_TYPE.EXPENSES
        ? -target_month_total_in_cents
        : target_month_total_in_cents
    ),
    previous_month_total: formatCurrencyFromCents(
      insightsType === INSIGHTS_TYPE.EXPENSES
        ? -previous_month_total_in_cents
        : previous_month_total_in_cents
    ),
    monthly_average: ytd_monthly_average_in_cents
      ? formatCurrencyFromCents(
          insightsType === INSIGHTS_TYPE.EXPENSES
            ? -ytd_monthly_average_in_cents
            : ytd_monthly_average_in_cents
        )
      : undefined,
    year_total: formatCurrencyFromCents(
      insightsType === INSIGHTS_TYPE.EXPENSES
        ? -ytd_total_in_cents
        : ytd_total_in_cents
    ),
    percent_change: percent_change_from_previous_month_decimal,
    percent_counterpart_change: percent_of_target_months_counterpart_decimal,
  }
}

export const formatEin = (ein?: string | null) => {
  if (!ein) {
    return ''
  }

  // We save the ein with a dash sometimes - if it comes back in the format don't add formatting
  return ein.includes('-')
    ? ein
    : patternFormatter(ein, { format: '##-#######' })
}

export const getIncomeOrExpenseLabel = (insightsType: INSIGHTS_TYPE) => {
  return insightsType === INSIGHTS_TYPE.INCOME ? 'Income' : 'Expenses'
}

export const insightsCopyConstants: {
  [type: string]: {
    [field: string]: string
  }
} = {
  [INSIGHTS_TYPE.EXPENSES]: {
    label: 'Expenses',
    currentPanelTitle: 'Current Expenses',
    emptyCurrentSummary:
      'How much your monthly expenses spending increases or decreases will show up here.',
    trackPageTitle: 'Expenses Over Time',
    emptyChart: 'Expense categories will show up here.',
    emptyChartMonth: 'No Expenses for this month',
    trackButton: 'clicked expenses page button',
  },
  [INSIGHTS_TYPE.INCOME]: {
    label: 'Income',
    currentPanelTitle: 'Current Income',
    emptyCurrentSummary:
      'How much your monthly income increases or decreases will show up here.',
    trackPageTitle: 'Income Over Time',
    emptyChart: 'Income categories will show up here.',
    emptyChartMonth: 'No Income for this month',
    trackButton: 'clicked income page button',
  },
  [INSIGHTS_TYPE.PROFIT]: {
    label: 'Profit',
    currentPanelTitle: 'Current Profit',
    emptyCurrentSummary:
      'How much your monthly profit increases or decreases will show up here.',
    trackPageTitle: 'Profit Over Time',
    emptyChart: 'Your profit will show up here.',
    emptyChartMonth: 'No Profit for this month',
    trackButton: 'clicked profit page button',
  },
}

export const insightsTrackDefaultFilters: {
  [type: string]: DropdownOption[]
} = {
  [INSIGHTS_TYPE.EXPENSES]: [
    { text: 'All Expenses', value: 'all' },
    { text: 'Top Expenses', value: 'top' },
  ],
  [INSIGHTS_TYPE.INCOME]: [{ text: 'All Categories', value: 'all' }],
  [INSIGHTS_TYPE.PROFIT]: [],
}

export enum FinancialInsightsButtonType {
  CLOSE_BANNER = 'close-banner',
  YEAR_FILTER = 'year-filter',
  MONTH_FILTER = 'month-filter',
  EXPENSES_FILTER = 'expenses-filter',
  CATEGORY_TRANSACTIONS = 'category-transactions',
  TABLE_PAGE = 'table-page',
  LEARN_MORE_CARD = 'learn-more',
  INCOME_FILTER = 'income-filter',
}
